import { Fragment, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import StatusBadge, { emailStatusTone } from "@/components/admin/StatusBadge";
import { Loader2, Mail, ShieldAlert } from "lucide-react";

interface EmailRow {
  id: string;
  message_id: string | null;
  template_name: string | null;
  recipient_email: string | null;
  status: string;
  error_message: string | null;
  created_at: string;
}

const RANGES = {
  "24h": 1,
  "7d": 7,
  "30d": 30,
} as const;
type RangeKey = keyof typeof RANGES;

const PAGE_SIZE = 50;
const FAILED_STATUSES = ["dlq", "failed", "bounced", "complained"];

// The deduped view isn't in generated types yet — cast through unknown.
const dedupedTable = () =>
  (supabase as unknown as {
    from: (t: string) => ReturnType<typeof supabase.from>;
  }).from("email_send_log_deduped");

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });

const AdminEmail = () => {
  const [rows, setRows] = useState<EmailRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({ total: 0, sent: 0, failed: 0, suppressed: 0 });
  const [templates, setTemplates] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<RangeKey>("7d");
  const [template, setTemplate] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [range, template, status, debouncedSearch]);

  const since = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - RANGES[range]);
    return d.toISOString();
  }, [range]);

  // Stats + template list (recomputed when range changes)
  useEffect(() => {
    let active = true;
    (async () => {
      const { data } = await dedupedTable()
        .select("status, template_name")
        .gte("created_at", since)
        .limit(10000);
      if (!active || !data) return;
      const s = { total: data.length, sent: 0, failed: 0, suppressed: 0 };
      const tset = new Set<string>();
      for (const r of data as { status: string; template_name: string | null }[]) {
        if (r.status === "sent") s.sent++;
        else if (FAILED_STATUSES.includes(r.status)) s.failed++;
        else if (r.status === "suppressed") s.suppressed++;
        if (r.template_name) tset.add(r.template_name);
      }
      setStats(s);
      setTemplates(Array.from(tset).sort());
    })();
    return () => {
      active = false;
    };
  }, [since]);

  // Paginated rows
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      let query = dedupedTable()
        .select("*", { count: "exact" })
        .gte("created_at", since)
        .order("created_at", { ascending: false })
        .range(from, to);

      if (template !== "all") query = query.eq("template_name", template);
      if (status !== "all") {
        if (status === "failed") query = query.in("status", FAILED_STATUSES);
        else query = query.eq("status", status);
      }
      const q = debouncedSearch.trim();
      if (q) {
        const esc = q.replace(/[,()]/g, " ");
        query = query.ilike("recipient_email", `%${esc}%`);
      }

      const { data, error, count } = await query;
      if (!active) return;
      if (!error && data) {
        setRows(data as unknown as EmailRow[]);
        setTotalCount(count ?? 0);
      }
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [since, template, status, debouncedSearch, page]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const pageNumbers = useMemo(() => {
    const pages: (number | "ellipsis")[] = [];
    const maxButtons = 7;
    if (totalPages <= maxButtons) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push("ellipsis");
      const start = Math.max(2, page - 1);
      const end = Math.min(totalPages - 1, page + 1);
      for (let i = start; i <= end; i++) pages.push(i);
      if (page < totalPages - 2) pages.push("ellipsis");
      pages.push(totalPages);
    }
    return pages;
  }, [page, totalPages]);

  return (
    <>
      <Helmet>
        <title>Email · Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Email</h1>
          <p className="text-muted-foreground mt-1">
            Delivery status of every email sent from the site.
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to="/admin/emails/suppressions">
            <ShieldAlert className="w-4 h-4 mr-2" /> Suppression list
          </Link>
        </Button>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: "Total", value: stats.total },
          { label: "Sent", value: stats.sent },
          { label: "Failed", value: stats.failed },
          { label: "Suppressed", value: stats.suppressed },
        ].map((s) => (
          <div key={s.label} className="rounded-lg border border-border bg-card p-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              {s.label}
            </div>
            <div className="text-2xl font-semibold mt-2">{s.value}</div>
          </div>
        ))}
      </section>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Select value={range} onValueChange={(v) => setRange(v as RangeKey)}>
          <SelectTrigger className="w-full sm:w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="24h">Last 24h</SelectItem>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
        <Select value={template} onValueChange={setTemplate}>
          <SelectTrigger className="w-full sm:w-56">
            <SelectValue placeholder="Template" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All templates</SelectItem>
            {templates.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="suppressed">Suppressed</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search recipient…"
          className="flex-1"
        />
      </div>

      <div className="rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
        {loading ? (
          <div className="p-10 flex items-center justify-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 flex flex-col items-center justify-center text-muted-foreground text-sm">
            <Mail className="w-8 h-8 mb-2 opacity-60" />
            No emails match your filters.
          </div>
        ) : (
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Template</th>
                <th className="text-left px-4 py-2 font-medium">Recipient</th>
                <th className="text-left px-4 py-2 font-medium">When</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const isFailed = FAILED_STATUSES.includes(r.status);
                const isOpen = expanded === r.id;
                return (
                  <Fragment key={r.id}>
                    <tr
                      onClick={() => isFailed && setExpanded(isOpen ? null : r.id)}
                      className={`border-t border-border ${
                        isFailed ? "cursor-pointer hover:bg-muted/30" : ""
                      }`}
                    >
                      <td className="px-4 py-3">
                        <StatusBadge label={r.status} tone={emailStatusTone(r.status)} />
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{r.template_name ?? "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{r.recipient_email ?? "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                        {fmtDate(r.created_at)}
                      </td>
                    </tr>
                    {isOpen && r.error_message && (
                      <tr className="bg-red-500/5">
                        <td colSpan={4} className="px-4 py-3 text-xs font-mono text-red-700 dark:text-red-300 whitespace-pre-wrap">
                          {r.error_message}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
        </div>
      </div>

      {totalPages > 1 && (
        <Pagination className="mt-4">
          <PaginationContent>
            <PaginationItem>
              <PaginationPrevious
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (page > 1) setPage(page - 1);
                }}
                aria-disabled={page === 1}
                className={page === 1 ? "pointer-events-none opacity-50" : ""}
              />
            </PaginationItem>
            {pageNumbers.map((p, i) =>
              p === "ellipsis" ? (
                <PaginationItem key={`e-${i}`}>
                  <PaginationEllipsis />
                </PaginationItem>
              ) : (
                <PaginationItem key={p}>
                  <PaginationLink
                    href="#"
                    isActive={p === page}
                    onClick={(e) => {
                      e.preventDefault();
                      setPage(p);
                    }}
                  >
                    {p}
                  </PaginationLink>
                </PaginationItem>
              ),
            )}
            <PaginationItem>
              <PaginationNext
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  if (page < totalPages) setPage(page + 1);
                }}
                aria-disabled={page === totalPages}
                className={page === totalPages ? "pointer-events-none opacity-50" : ""}
              />
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      )}
    </>
  );
};

export default AdminEmail;
